import type { ScrapingStatus } from '@repo/types';
import { cn } from '@/lib/utils';

interface ScrapingStatusBadgeProps {
  status: ScrapingStatus;
  className?: string;
  showIcon?: boolean;
}

const CONFIG: Record<
  ScrapingStatus,
  { label: string; dot: string; badge: string; pulse?: boolean }
> = {
  pending: {
    label: 'Pending',
    dot: 'bg-slate-400',
    badge: 'border-border bg-muted text-muted-foreground',
  },
  processing: {
    label: 'Processing',
    dot: 'bg-blue-500',
    badge: 'border-blue-500/20 bg-blue-500/10 text-blue-700 dark:text-blue-400',
    pulse: true,
  },
  completed: {
    label: 'Completed',
    dot: 'bg-emerald-500',
    badge: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  },
  failed: {
    label: 'Failed',
    dot: 'bg-red-500',
    badge: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-400',
  },
  retrying: {
    label: 'Retrying',
    dot: 'bg-amber-500',
    badge: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-400',
    pulse: true,
  },
};

export function ScrapingStatusBadge({ status, className, showIcon = true }: ScrapingStatusBadgeProps) {
  const config = CONFIG[status] ?? CONFIG.pending;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        config.badge,
        className
      )}
    >
      {showIcon && (
        <span className="relative flex size-1.5 shrink-0">
          {/* Live states (processing / retrying) emit an expanding ring — reads as
              "actively working" far more clearly than a faint opacity pulse.
              Disabled automatically under prefers-reduced-motion (global guard). */}
          {config.pulse && (
            <span
              aria-hidden
              className={cn(
                'absolute inline-flex h-full w-full animate-ping rounded-full opacity-75',
                config.dot
              )}
            />
          )}
          <span className={cn('relative inline-flex size-1.5 rounded-full', config.dot)} />
        </span>
      )}
      {config.label}
    </span>
  );
}
